import { useMemo } from 'react';

import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import Typography from '@/components/Typography';
import { pastExportsOptions } from '@/features/DataHub/api';
import { DataHubHeader } from '@/features/DataHub/components/DataHubHeader';
import { DATAHUB_TITLES } from '@/features/DataHub/constants/titles';
import BasicUserView from '@/features/DataHub/pages/PastExports/components/BasicUserView';
import { ExportItem } from '@/features/DataHub/pages/PastExports/components/ExportItem';
import { ExportItemsSkeleton } from '@/features/DataHub/pages/PastExports/components/ExportItemsSkeleton';
import { PastExportsEmptyState } from '@/features/DataHub/pages/PastExports/components/PastExportsEmptyState';
import useDocumentTitleRename from '@/hooks/useDocumentTitleRename';
import useUserDetails from '@/hooks/useUserDetails';

export const PastExports = () => {
  useDocumentTitleRename(DATAHUB_TITLES.pastExports);
  const { data, isError, isLoading, error } = useQuery(pastExportsOptions.list());
  const isEmpty = data?.length === 0;
  const { isUserBasic, isUserExpired } = useUserDetails();
  const sortedData = useMemo(
    () => data?.toSorted((a, b) => b.lastExecutionTime - a.lastExecutionTime),
    [data]
  );
  if (isUserBasic || isUserExpired) {
    return <BasicUserView />;
  }
  if (isEmpty || (error instanceof AxiosError && error.status === 404))
    return <PastExportsEmptyState />;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        flex: 1,
        p: '24px',
        overflow: 'auto'
      }}
    >
      <DataHubHeader
        subtitle='Your past exports with details'
        title='Past Exports'
      />
      {isLoading && <ExportItemsSkeleton />}
      {isError && <Typography>Error loading data</Typography>}

      {sortedData?.map(item => (
        <ExportItem
          key={item.id}
          item={item}
        />
      ))}
    </Box>
  );
};
